import { useEffect, useState, useRef, Fragment } from 'react'
import { useAuth } from '../hooks/useAuth'
import { useIsBrandAdmin } from '../lib/brandAdmin'
import { searchFilter } from '../lib/search'
import { supabase } from '../lib/supabase'
import { Plus, X, Edit2, Warehouse, ArrowRight, BarChart2, Truck, Package, Trash2, Copy, Check, Download, Search, ChevronDown } from 'lucide-react'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'
import { exportToCsv } from '../lib/exportCsv'
import ProductPicker from '../components/ProductPicker'
import HelpLink from '../components/HelpLink'

// TABS built dynamically below based on locations

const TO_ACTIVE_STATUSES = ['planned', 'shipped']
const TO_STATUS_LABEL = { planned: 'Planned', shipped: 'Shipped', cancelled: 'Canceled' }
const DO_ACTIVE_STATUSES = ['draft', 'packed']

function StatusFilter({ statuses, selected, onChange, activeStatuses }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    function onClickOutside(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [])
  const toggle = st => onChange(selected.includes(st) ? selected.filter(x => x !== st) : [...selected, st])
  const label =
    selected.length === statuses.length ? 'All statuses'
    : selected.length === 0 ? 'No statuses'
    : activeStatuses && selected.length === activeStatuses.length && activeStatuses.every(s => selected.includes(s)) ? 'Active'
    : `${selected.length} statuses`
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button className="btn btn-secondary" onClick={() => setOpen(o => !o)} style={{ minWidth: '150px', justifyContent: 'space-between' }}>
        {label}
        <ChevronDown size={14} />
      </button>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 30, background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', boxShadow: '0 8px 24px rgba(0,0,0,0.35)', padding: '6px', minWidth: '200px' }}>
          <div style={{ display: 'flex', gap: '4px', padding: '2px 4px 8px', borderBottom: '1px solid var(--border)', marginBottom: '4px' }}>
            <button className="btn btn-ghost btn-sm" onClick={() => onChange([...statuses])}>All</button>
            {activeStatuses && <button className="btn btn-ghost btn-sm" onClick={() => onChange([...activeStatuses])}>Active</button>}
            <button className="btn btn-ghost btn-sm" onClick={() => onChange([])}>None</button>
          </div>
          {statuses.map(s => (
            <label key={s} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 8px', borderRadius: 'var(--radius)', cursor: 'pointer', fontSize: '13px', textTransform: 'capitalize' }}>
              <input type="checkbox" checked={selected.includes(s)} onChange={() => toggle(s)} />
              {s.replace(/_/g, ' ')}
            </label>
          ))}
        </div>
      )}
    </div>
  )
}

const REASONS = [
  { value: 'received', label: 'Received' },
  { value: 'sold', label: 'Sold' },
  { value: 'returned', label: 'Returned' },
  { value: 'damaged', label: 'Damaged' },
  { value: 'theft_loss', label: 'Theft / Loss' },
  { value: 'restock', label: 'Restock' },
  { value: 'correction', label: 'Correction' },
  { value: 'other', label: 'Other' },
]

const LOCATION_TYPES = [
  { value: '1pl', label: '1PL' },
  { value: '3pl', label: '3PL' },
  { value: 'amazon_fba', label: 'Amazon FBA' },
  { value: 'supplier', label: 'Supplier / Co-Mfr' },
]

function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false)
  return (
    <button onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500) }}
      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 3px', color: copied ? 'var(--success)' : 'var(--text-3)', verticalAlign: 'middle' }}>
      {copied ? <Check size={11} /> : <Copy size={11} />}
    </button>
  )
}

// Brands whose old days-of-supply reorder alerts are paused in favour of the Planner page
const REORDER_ALERTS_PAUSED = ['bloomi']

function UploadAsOf({ snapshots, locationId }) {
  const s = snapshots.find(x => x.location_id === locationId)
  const fmt = d => format(new Date(d.length === 10 ? d + 'T12:00:00' : d), 'MMM d, yyyy')
  return (
    <div style={{ fontSize: '12px', color: 'var(--text-3)' }}>
      {s ? <>Inventory as of <strong style={{ color: 'var(--text-2)' }}>{fmt(s.as_of_date)}</strong> · uploaded {fmt(s.created_at)}{s.file_name ? ` · ${s.file_name}` : ''}</> : 'No inventory upload yet for this location'}
    </div>
  )
}

// `only` renders one section as its own page (sidebar: Transfer Orders, Locations) without the Inventory tabs
export default function Inventory({ only }) {
  const { activeBrand, user } = useAuth()
  const isBrandAdmin = useIsBrandAdmin()
  const [tab, setTab] = useState(only || '__warehouse__')
  const [snapshots, setSnapshots] = useState([])
  // If "View as brand_admin" is switched on while a hidden tab is open, fall back to the first location tab
  useEffect(() => {
    if (only) { if (tab !== only) setTab(only); return }
    // brand_admins get Locations and Transfer Orders as their own sidebar pages
    if (isBrandAdmin && ['amazon_replen', 'alerts', 'transactions', 'locations', 'transfers', 'disposals'].includes(tab)) setTab('__warehouse__')
  }, [isBrandAdmin, tab, only])
  const [search, setSearch] = useState('')
  const [sortCol, setSortCol] = useState(null)
  const [sortDir, setSortDir] = useState('asc')
  const [alertSortCol, setAlertSortCol] = useState(null)
  const [alertSortDir, setAlertSortDir] = useState('asc')
  const [expirySortCol, setExpirySortCol] = useState(null)
  const [expirySortDir, setExpirySortDir] = useState('asc')
  const [products, setProducts] = useState([])
  const [locations, setLocations] = useState([])
  const [onHand, setOnHand] = useState([]) // {product_id, location_id, on_hand}
  const [transactions, setTransactions] = useState([])
  const [sellThrough, setSellThrough] = useState([])
  const [transferOrders, setTransferOrders] = useState([])
  const [toStatusFilter, setToStatusFilter] = useState(TO_ACTIVE_STATUSES)
  const [doStatusFilter, setDoStatusFilter] = useState(DO_ACTIVE_STATUSES)
  const [inTransitItems, setInTransitItems] = useState([])
  const [poLines, setPoLines] = useState([])
  const [wholesaleLines, setWholesaleLines] = useState([])
  const [sellable, setSellable] = useState([])
  const [expiryLots, setExpiryLots] = useState([])
  const [allLots, setAllLots] = useState([])
  const [bom, setBom] = useState([])
  const [manualUpdates, setManualUpdates] = useState({}) // `${product_id}|${location_id}` -> latest manual created_at
  const [loading, setLoading] = useState(true)

  // Modals
  const [adjModal, setAdjModal] = useState(false)
  const [adjForm, setAdjForm] = useState({ product_id: '', location_id: '', mode: 'new_qty', new_qty: '', adjustment: '', reason: 'correction', notes: '', lot_number: '', expiration_date: '' })
  const [adjSaving, setAdjSaving] = useState(false)

  const [locModal, setLocModal] = useState(false)
  const [locForm, setLocForm] = useState({ name: '', type: '3pl', address: '', notes: '' })
  const [locEditing, setLocEditing] = useState(null)
  const [locSaving, setLocSaving] = useState(false)

  const [strModal, setStrModal] = useState(false)
  const [strForm, setStrForm] = useState({ product_id: '', shopify_units_28d: '', amazon_units_14d: '' })
  const [strSaving, setStrSaving] = useState(false)

  const [disposalOrders, setDisposalOrders] = useState([])
  const [doModal, setDoModal] = useState(false)
  const [doEditId, setDoEditId] = useState(null)
  const EMPTY_DO = { disposal_number: '', from_location_id: '', status: 'draft', expected_completion_date: '', actual_completion_date: '', notes: '' }
  const [doForm, setDoForm] = useState(EMPTY_DO)
  const [doLines, setDoLines] = useState([{ product_id: '', quantity: '' }])
  const [doSaving, setDoSaving] = useState(false)

  const [toModal, setToModal] = useState(false)
  const [toEditId, setToEditId] = useState(null)
  const EMPTY_TO = { transfer_number: '', from_location_id: '', to_location_id: '', status: 'planned', expected_ship_date: '', ship_date: '', expected_arrival: '', carrier: '', tracking_number: '', notes: '' }
  const [toForm, setToForm] = useState(EMPTY_TO)
  const [toLines, setToLines] = useState([{ product_id: '', quantity: '' }])
  const [toSaving, setToSaving] = useState(false)
  const [toDetail, setToDetail] = useState(null)

  useEffect(() => { if (activeBrand) load() }, [activeBrand])

  async function load() {
    setLoading(true)
    const [
      { data: prods },
      { data: locs },
      { data: oh },
      { data: txns },
      { data: str },
      { data: tos },
      { data: disposalData },
      { data: poLinesData },
      { data: inTransitShipments },
      { data: sellableData },
      { data: expiryData },
      { data: bomData },
      { data: lotsData },
      { data: manualTx }
    ] = await Promise.all([
      supabase.from('products').select('id, sku, name, asin, product_type, sourcing_type, production_lead_time_days, target_days_of_supply, fba_target_days, reorder_point, replenishment_status, channels, shopify_enabled, shopify_replen_status, amazon_enabled, amazon_replen_status').eq('is_active', true).eq('brand_id', activeBrand.id).is('deleted_at', null).order('sku'),
      supabase.from('locations').select('*').eq('brand_id', activeBrand.id).eq('is_active', true).order('name'),
      supabase.from('inventory_on_hand').select('*').eq('brand_id', activeBrand.id),
      supabase.from('inventory_transactions').select('*, products(name, sku), locations(name), user_profiles(full_name)').eq('brand_id', activeBrand.id).order('created_at', { ascending: false }).limit(200),
      supabase.from('sell_through_rates').select('id, product_id, shopify_units_28d, amazon_units_14d, shopify_str_source, updated_at').eq('brand_id', activeBrand.id),
      supabase.from('transfer_orders').select('*, from_loc:from_location_id(name), to_loc:to_location_id(name), transfer_order_lines(product_id, quantity, products(sku, name))').eq('brand_id', activeBrand.id).order('created_at', { ascending: false }),
      supabase.from('disposal_orders').select('*, from_loc:from_location_id(name), disposal_order_lines(product_id, quantity, products(sku, name))').eq('brand_id', activeBrand.id).order('created_at', { ascending: false }),
      supabase.from('po_line_items').select('product_id, quantity_ordered, quantity_received, purchase_orders!inner(brand_id, status)').eq('purchase_orders.brand_id', activeBrand.id).in('purchase_orders.status', ['sent', 'confirmed', 'partially_received']),
      supabase.from('shipments').select('id, ship_to_location_id').eq('brand_id', activeBrand.id).in('status', ['in_transit', 'arrived', 'receiving']).eq('is_direct_ship', false),
      supabase.from('inventory_sellable').select('*').eq('brand_id', activeBrand.id),
      supabase.from('inventory_lots_view').select('*, products(sku, name, min_shelf_life_days), locations(name)').eq('brand_id', activeBrand.id).not('expiration_date', 'is', null).order('expiration_date'),
      supabase.from('bill_of_materials').select('parent_product_id, component_product_id, quantity').eq('brand_id', activeBrand.id),
      supabase.from('product_lots').select('product_id, location_id, lot_number, expiration_date, quantity, bin_location, products(sku, name)').eq('brand_id', activeBrand.id).order('product_id'),
      supabase.from('inventory_transactions').select('product_id, location_id, created_at').eq('brand_id', activeBrand.id).eq('source', 'manual').order('created_at', { ascending: false })
    ])
    setProducts(prods || [])
    setLocations(locs || [])
    setOnHand(oh || [])
    setTransactions(txns || [])
    setSellThrough(str || [])
    setSellable(sellableData || [])
    setTransferOrders(tos || [])
    setDisposalOrders(disposalData || [])
    setPoLines(poLinesData || [])
    setBom(bomData || [])
    // Wholesale lines not yet removed from Shopify reduce available qty.
    // Wrapped separately + error-tolerant so a missing wholesale table can't break Inventory.
    const { data: wsLinesData } = await supabase.from('wholesale_order_lines')
      .select('product_id, quantity, shopify_already_deducted, wholesale_orders!inner(brand_id, status)')
      .eq('wholesale_orders.brand_id', activeBrand.id)
      .eq('shopify_already_deducted', false)
      .not('wholesale_orders.status', 'in', '("closed","cancelled")')
    setWholesaleLines(wsLinesData || [])
    // Latest inventory upload per location (Planning Inputs) — shown at the top of each location tab
    const { data: snapData } = await supabase.from('inventory_snapshots')
      .select('location_id, as_of_date, created_at, file_name')
      .eq('brand_id', activeBrand.id).order('created_at', { ascending: false }).limit(50)
    setSnapshots(snapData || [])
    const manualMap = {}
    ;(manualTx || []).forEach(t => {
      const key = `${t.product_id}|${t.location_id}`
      if (!manualMap[key]) manualMap[key] = t.created_at // first = latest (ordered desc)
    })
    setManualUpdates(manualMap)
    // Load line items for in-transit shipments
    const inTransitShipmentIds = (inTransitShipments || []).map(s => s.id)
    let inTransitLineItems = []
    if (inTransitShipmentIds.length > 0) {
      const { data: itLines } = await supabase.from('shipment_line_items')
        .select('product_id, quantity_received, shipment_id')
        .in('shipment_id', inTransitShipmentIds)
      // Attach ship_to_location_id from shipment data
      const shipmentMap = {}
      ;(inTransitShipments || []).forEach(s => { shipmentMap[s.id] = s })
      inTransitLineItems = (itLines || []).map(l => ({
        ...l,
        ship_to_location_id: shipmentMap[l.shipment_id]?.ship_to_location_id
      }))
    }
    setExpiryLots(expiryData || [])
    setAllLots(lotsData || [])
    setInTransitItems(inTransitLineItems)
    setLoading(false)
  }

  // ── Sort ──
  function toggleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortCol(col); setSortDir('asc') }
  }

  function sortIcon(col) {
    if (sortCol !== col) return <span style={{ color: 'var(--text-3)', fontSize: '10px', marginLeft: '3px' }}>↕</span>
    return <span style={{ color: 'var(--accent)', fontSize: '10px', marginLeft: '3px' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>
  }

  function sortProducts(prods, getVal) {
    if (!sortCol) return prods
    return [...prods].sort((a, b) => {
      const av = getVal(a) ?? -Infinity
      const bv = getVal(b) ?? -Infinity
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av)
      return sortDir === 'asc' ? av - bv : bv - av
    })
  }

  function toggleAlertSort(col) {
    if (alertSortCol === col) setAlertSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setAlertSortCol(col); setAlertSortDir('asc') }
  }
  function alertSortIcon(col) {
    if (alertSortCol !== col) return <span style={{ color: 'var(--text-3)', fontSize: '10px', marginLeft: '3px' }}>↕</span>
    return <span style={{ color: 'var(--accent)', fontSize: '10px', marginLeft: '3px' }}>{alertSortDir === 'asc' ? '↑' : '↓'}</span>
  }
  function sortAlerts(rows) {
    if (!alertSortCol) return rows
    return [...rows].sort((a, b) => {
      const getVal = r => {
        if (alertSortCol === 'sku') return r.product.sku
        if (alertSortCol === 'name') return r.product.name
        if (alertSortCol === 'daily') return r.daily
        if (alertSortCol === 'onhand') return r.totalOnHand
        if (alertSortCol === 'intransit') return r.inTransit
        if (alertSortCol === 'onorder') return r.onOrder
        if (alertSortCol === 'totalSupply') return r.totalSupplyDays
        if (alertSortCol === 'fbaDays') return r.fbaDays
        if (alertSortCol === 'fbaNeeded') return r.fbaNeeded
        if (alertSortCol === 'netSupply') return r.netSupplyDays
        return null
      }
      const av = getVal(a) ?? -Infinity
      const bv = getVal(b) ?? -Infinity
      if (typeof av === 'string') return alertSortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av)
      return alertSortDir === 'asc' ? av - bv : bv - av
    })
  }

  function toggleExpirySort(col) {
    if (expirySortCol === col) setExpirySortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setExpirySortCol(col); setExpirySortDir('asc') }
  }
  function expirySortIcon(col) {
    if (expirySortCol !== col) return <span style={{ color: 'var(--text-3)', fontSize: '10px', marginLeft: '3px' }}>↕</span>
    return <span style={{ color: 'var(--accent)', fontSize: '10px', marginLeft: '3px' }}>{expirySortDir === 'asc' ? '↑' : '↓'}</span>
  }
  function sortExpiryRows(rows) {
    if (!expirySortCol) return rows
    return [...rows].sort((a, b) => {
      const getVal = r => {
        if (expirySortCol === 'sku') return r.product?.sku || ''
        if (expirySortCol === 'name') return r.product?.name || ''
        if (expirySortCol === 'lot') return r.lot.lot_number
        if (expirySortCol === 'expiry') return r.lot.expiration_date || ''
        if (expirySortCol === 'daysLeft') return r.daysLeft
        if (expirySortCol === 'dailyRate') return r.dailyRate
        if (expirySortCol === 'projectedSold') return r.projectedSold
        if (expirySortCol === 'atRisk') return r.atRisk
        if (expirySortCol === 'severity') return ['ok','low','medium','high','expired'].indexOf(r.severity)
        return null
      }
      const av = getVal(a) ?? -Infinity
      const bv = getVal(b) ?? -Infinity
      if (typeof av === 'string') return expirySortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av)
      return expirySortDir === 'asc' ? av - bv : bv - av
    })
  }

  // ── Helpers ──
  // A complete SKU or ASIN shows only that product; partial text matches SKU, description or ASIN (lib/search)
  const searchMatch = searchFilter(products, search, (p, q) => (p.sku || '').toLowerCase().includes(q) || (p.name || '').toLowerCase().includes(q) || (p.asin || '').toLowerCase().includes(q))
  function matchesSearch(p) { return searchMatch(p) }

  function getOnHand(productId, locationId) {
    const row = onHand.find(r => r.product_id === productId && r.location_id === locationId)
    return row ? Number(row.on_hand) : 0
  }

  function getLastManualUpdate(productId, locationId) {
    return manualUpdates[`${productId}|${locationId}`] || null
  }

  function getTotalOnHand(productId) {
    return onHand.filter(r => r.product_id === productId).reduce((sum, r) => sum + Number(r.on_hand), 0)
  }

  function getSellable(productId, locationId) {
    const row = sellable.find(r => r.product_id === productId && r.location_id === locationId)
    return row ? { sellable: Number(row.sellable_qty || 0), atRisk: Number(row.at_risk_qty || 0), expired: Number(row.expired_qty || 0) } : null
  }

  function getSellableTotal(productId) {
    return sellable.filter(r => r.product_id === productId).reduce((sum, r) => sum + Number(r.sellable_qty || 0), 0)
  }

  function getInTransit(productId, locationId) {
    // If locationId provided, filter to shipments going to that location
    // Otherwise return all in-transit (for total supply calculation)
    return inTransitItems
      .filter(i => {
        if (i.product_id !== productId) return false
        if (locationId) {
          return i.ship_to_location_id === locationId
        }
        return true
      })
      .reduce((sum, i) => sum + Number(i.quantity_received || 0), 0)
  }

  function getOnOrder(productId) {
    return poLines
      .filter(l => l.product_id === productId)
      .reduce((sum, l) => sum + Math.max(0, Number(l.quantity_ordered) - Number(l.quantity_received || 0)), 0)
  }

  function getWholesaleAllocated(productId) {
    return wholesaleLines
      .filter(l => l.product_id === productId)
      .reduce((sum, l) => sum + Number(l.quantity || 0), 0)
  }

  function getDailyRate(productId) {
    const str = sellThrough.find(s => s.product_id === productId)
    if (!str) return 0
    const shopifyDaily = (str.shopify_units_28d || 0) / 28
    const amazonDaily = (str.amazon_units_14d || 0) / 14
    return shopifyDaily + amazonDaily
  }

  function getChannelDailyRate(product) {
    const str = sellThrough.find(s => s.product_id === product.id)
    if (!str) return 0
    const shopify = product.shopify_enabled !== false ? (str.shopify_units_28d || 0) / 28 : 0
    const amazon = product.amazon_enabled ? (str.amazon_units_14d || 0) / 14 : 0
    return shopify + amazon
  }

  function getShopifyDailyRate(productId) {
    const str = sellThrough.find(s => s.product_id === productId)
    if (!str) return 0
    return (str.shopify_units_28d || 0) / 28
  }

  function getAmazonDailyRate(productId) {
    const str = sellThrough.find(s => s.product_id === productId)
    if (!str) return 0
    return (str.amazon_units_14d || 0) / 14
  }

  function getDaysOnHand(productId) {
    const available = getTotalOnHand(productId)
    const daily = getDailyRate(productId)
    if (!daily) return null
    return Math.round(available / daily)
  }

  function getTotalSupply(productId) {
    return getTotalOnHand(productId) + getInTransit(productId) + getOnOrder(productId)
  }

  function getTotalSupplyDays(productId) {
    const daily = getDailyRate(productId)
    if (!daily) return null
    return Math.round(getTotalSupply(productId) / daily)
  }

  // Returns parent virtual bundles that drive demand for this component (for display context)
  function getBomBundleContext(productId) {
    return bom
      .filter(b => b.component_product_id === productId)
      .map(b => {
        const bundle = products.find(p => p.id === b.parent_product_id && p.product_type === 'virtual_bundle')
        if (!bundle) return null
        const bundleDaily = getChannelDailyRate(bundle)
        return bundleDaily > 0 ? { bundle, qty: b.quantity, addedDaily: bundleDaily * b.quantity } : null
      })
      .filter(Boolean)
  }

  function getProductAlerts(product) {
    // Reorder / FBA replenishment alerts are paused for brands that use the Planner (weeks-of-supply engine).
    // Expiry alerts are unaffected. Remove the brand from REORDER_ALERTS_PAUSED to turn them back on.
    if (REORDER_ALERTS_PAUSED.includes(activeBrand?.slug)) return null
    const daily = getChannelDailyRate(product)
    const bothSunset = product.shopify_replen_status === 'sunset' && product.amazon_replen_status === 'sunset'
    const shopifyActive = product.shopify_enabled !== false && product.shopify_replen_status !== 'sunset'
    const amazonActive = product.amazon_enabled && product.amazon_replen_status !== 'sunset'
    if (!daily || bothSunset) return null

    const transitDays = product.sourcing_type === 'domestic' ? 7 : 45
    const productionDays = product.production_lead_time_days || 45
    const totalLeadTime = productionDays + transitDays + 30
    const targetDays = product.target_days_of_supply || 90
    const fbaTarget = product.fba_target_days || 60

    // FBA calculation
    const fbaLocation = locations.find(l => l.type === 'amazon_fba')
    const fbaOnHand = fbaLocation ? getOnHand(product.id, fbaLocation.id) : 0
    const fbaDays = fbaOnHand > 0 ? Math.round(fbaOnHand / daily) : 0
    const fbaNeeded = Math.max(0, Math.ceil((daily * fbaTarget) - fbaOnHand))
    const warehouseTotal = locations.filter(l => ['1pl', '3pl'].includes(l.type)).reduce((sum, l) => sum + getOnHand(product.id, l.id), 0)
    const fbaAlert = fbaDays < fbaTarget && daily > 0 && warehouseTotal > 0 && product.amazon_enabled && product.amazon_replen_status !== 'sunset'

    // Total supply
    const totalOnHand = getTotalOnHand(product.id)
    const inTransit = getInTransit(product.id)
    const onOrder = getOnOrder(product.id)
    const totalSupply = totalOnHand + inTransit + onOrder
    const totalSupplyDays = Math.round(totalSupply / daily)

    // Net warehouse after FBA replenishment
    const warehouseLocs = locations.filter(l => ['1pl', '3pl'].includes(l.type))
    const warehouseOnHand = warehouseLocs.reduce((sum, l) => sum + getOnHand(product.id, l.id), 0)
    const netWarehouseAfterFBA = Math.max(0, warehouseOnHand - fbaNeeded)
    const netTotalAfterFBA = netWarehouseAfterFBA + fbaOnHand + inTransit + onOrder
    const netSupplyDays = Math.round(netTotalAfterFBA / daily)

    // Reorder: use net supply after FBA replenishment
    const reorderAlert = netSupplyDays < totalLeadTime && shopifyActive
    const suggestedOrderQty = reorderAlert
      ? Math.max(0, Math.ceil((daily * targetDays) - netTotalAfterFBA))
      : 0

    if (!fbaAlert && !reorderAlert) return null

    return {
      product,
      daily,
      totalOnHand,
      inTransit,
      onOrder,
      totalSupply,
      totalSupplyDays,
      fbaOnHand,
      fbaDays,
      fbaTarget,
      fbaNeeded,
      fbaAlert,
      netWarehouseAfterFBA,
      netSupplyDays,
      reorderAlert,
      suggestedOrderQty,
      totalLeadTime,
      reorderSeverity: netSupplyDays < (totalLeadTime - 30) ? 'danger' : 'warning',
      fbaSeverity: fbaDays < 30 ? 'danger' : 'warning',
      bundleContext: getBomBundleContext(product.id)
    }
  }

  // Keep these for warehouse/FBA tab compatibility
  function getReorderAlert(product) {
    const a = getProductAlerts(product)
    if (!a || !a.reorderAlert) return null
    return { severity: a.reorderSeverity, suggestedQty: a.suggestedOrderQty, totalSupplyDays: a.netSupplyDays, leadTime: a.totalLeadTime }
  }

  function getFBAAlert(product) {
    const a = getProductAlerts(product)
    if (!a || !a.fbaAlert) return null
    return { severity: a.fbaSeverity, suggestedQty: a.fbaNeeded, fbaDays: a.fbaDays, fbaTarget: a.fbaTarget }
  }

  const allAlerts = products.filter(p => p.product_type !== 'virtual_bundle').map(p => getProductAlerts(p)).filter(Boolean)

  // ── Adjustment ──
  async function saveAdjustment() {
    setAdjSaving(true)
    const currentQty = getOnHand(adjForm.product_id, adjForm.location_id)
    let qty
    if (adjForm.mode === 'new_qty') {
      qty = parseFloat(adjForm.new_qty) - currentQty
    } else {
      qty = parseFloat(adjForm.adjustment)
    }
    await supabase.from('inventory_transactions').insert({
      brand_id: activeBrand.id,
      product_id: adjForm.product_id,
      location_id: adjForm.location_id,
      transaction_type: adjForm.reason,
      quantity: qty,
      notes: adjForm.notes,
      lot_number: adjForm.lot_number || null,
      expiration_date: adjForm.expiration_date || null,
      created_by: user?.id,
      source: 'manual'
    })
    setAdjSaving(false)
    setAdjModal(false)
    setAdjForm({ product_id: '', location_id: '', mode: 'new_qty', new_qty: '', adjustment: '', reason: 'correction', notes: '', lot_number: '', expiration_date: '' })
    load()
  }

  // ── Location ──
  async function saveLocation() {
    setLocSaving(true)
    const payload = { ...locForm, brand_id: activeBrand.id }
    if (locEditing) await supabase.from('locations').update(payload).eq('id', locEditing.id)
    else await supabase.from('locations').insert(payload)
    setLocSaving(false); setLocModal(false); setLocEditing(null)
    setLocForm({ name: '', type: '3pl', address: '', notes: '' })
    load()
  }

  // ── Sell-Through ──
  async function saveSellThrough() {
    setStrSaving(true)
    const existing = sellThrough.find(s => s.product_id === strForm.product_id)
    const payload = {
      brand_id: activeBrand.id,
      product_id: strForm.product_id,
      shopify_units_28d: parseFloat(strForm.shopify_units_28d) || 0,
      amazon_units_14d: parseFloat(strForm.amazon_units_14d) || 0,
      updated_at: new Date().toISOString(),
      updated_by: user?.id
    }
    if (existing) await supabase.from('sell_through_rates').update(payload).eq('id', existing.id)
    else await supabase.from('sell_through_rates').insert(payload)
    setStrSaving(false); setStrModal(false)
    setStrForm({ product_id: '', shopify_units_28d: '', amazon_units_14d: '' })
    load()
  }

  // ── Transfer Order ──
  function openNewTO() {
    setToEditId(null)
    setToForm(EMPTY_TO)
    setToLines([{ product_id: '', quantity: '' }])
    setToModal(true)
  }

  function openEditTO(to) {
    setToEditId(to.id)
    setToForm({
      transfer_number: to.transfer_number || '',
      from_location_id: to.from_location_id || '',
      to_location_id: to.to_location_id || '',
      status: to.status || 'planned',
      expected_ship_date: to.expected_ship_date || '',
      ship_date: to.ship_date || '',
      expected_arrival: to.expected_arrival || '',
      carrier: to.carrier || '',
      tracking_number: to.tracking_number || '',
      notes: to.notes || '',
    })
    const lines = (to.transfer_order_lines || []).map(l => ({ product_id: l.product_id || '', quantity: l.quantity || '' }))
    setToLines(lines.length > 0 ? lines : [{ product_id: '', quantity: '' }])
    setToModal(true)
  }

  function toPayload(includeStatus = false) {
    return {
      transfer_number: toForm.transfer_number,
      from_location_id: toForm.from_location_id,
      to_location_id: toForm.to_location_id,
      ...(includeStatus ? { status: toForm.status } : {}),
      expected_ship_date: toForm.expected_ship_date || null,
      // a shipped transfer always needs a ship date (the Planner compares it to the upload dates)
      ship_date: toForm.ship_date || (includeStatus && toForm.status === 'shipped' ? new Date().toISOString().split('T')[0] : null),
      expected_arrival: toForm.expected_arrival || null,
      carrier: toForm.carrier || null,
      tracking_number: toForm.tracking_number || null,
      notes: toForm.notes || null,
    }
  }

  async function saveTransferOrder() {
    setToSaving(true)
    const { data: to } = await supabase.from('transfer_orders').insert({
      brand_id: activeBrand.id,
      ...toPayload(),
      status: 'planned',
      created_by: user?.id
    }).select().single()

    if (to) {
      const validLines = toLines.filter(l => l.product_id && l.quantity)
      if (validLines.length > 0) {
        await supabase.from('transfer_order_lines').insert(
          validLines.map(l => ({ transfer_order_id: to.id, product_id: l.product_id, quantity: parseFloat(l.quantity) }))
        )
      }
    }
    setToSaving(false); setToModal(false); setToEditId(null)
    setToForm(EMPTY_TO); setToLines([{ product_id: '', quantity: '' }])
    load()
  }

  async function updateTransferOrder() {
    setToSaving(true)
    await supabase.from('transfer_orders').update(toPayload(true)).eq('id', toEditId)
    await supabase.from('transfer_order_lines').delete().eq('transfer_order_id', toEditId)
    const validLines = toLines.filter(l => l.product_id && l.quantity)
    if (validLines.length > 0) {
      await supabase.from('transfer_order_lines').insert(
        validLines.map(l => ({ transfer_order_id: toEditId, product_id: l.product_id, quantity: parseFloat(l.quantity) }))
      )
    }
    setToSaving(false); setToModal(false); setToEditId(null)
    setToForm(EMPTY_TO); setToLines([{ product_id: '', quantity: '' }])
    load()
  }

  // Transfers are Planned → Shipped (or Canceled). No receiving step and no inventory transactions:
  // the Shopify and FBA uploads are the source of truth, and the Planner works out what is still in transit.
  async function markTransferShipped(to) {
    await supabase.from('transfer_orders').update({ status: 'shipped', ship_date: to.ship_date || new Date().toISOString().split('T')[0] }).eq('id', to.id)
    load()
  }

  const TO_STATUSES = ['planned', 'shipped', 'cancelled']

  // ── Disposal Orders ──
  const DO_STATUSES = ['draft', 'packed', 'completed']
  const DO_STATUS_BADGE = { draft: 'neutral', packed: 'warning', completed: 'success' }

  async function saveDisposalOrder() {
    setDoSaving(true)
    const { data: doo } = await supabase.from('disposal_orders').insert({
      brand_id: activeBrand.id,
      disposal_number: doForm.disposal_number,
      from_location_id: doForm.from_location_id || null,
      status: 'draft',
      expected_completion_date: doForm.expected_completion_date || null,
      actual_completion_date: doForm.actual_completion_date || null,
      notes: doForm.notes || null,
      created_by: user?.id
    }).select().single()
    if (doo) {
      const validLines = doLines.filter(l => l.product_id && l.quantity)
      if (validLines.length > 0) {
        await supabase.from('disposal_order_lines').insert(
          validLines.map(l => ({ disposal_order_id: doo.id, product_id: l.product_id, quantity: parseFloat(l.quantity) }))
        )
      }
    }
    setDoSaving(false); setDoModal(false); setDoEditId(null)
    setDoForm(EMPTY_DO); setDoLines([{ product_id: '', quantity: '' }])
    load()
  }

  async function updateDisposalOrder() {
    setDoSaving(true)
    await supabase.from('disposal_orders').update({
      disposal_number: doForm.disposal_number,
      from_location_id: doForm.from_location_id || null,
      status: doForm.status,
      expected_completion_date: doForm.expected_completion_date || null,
      actual_completion_date: doForm.actual_completion_date || null,
      notes: doForm.notes || null,
    }).eq('id', doEditId)
    await supabase.from('disposal_order_lines').delete().eq('disposal_order_id', doEditId)
    const validLines = doLines.filter(l => l.product_id && l.quantity)
    if (validLines.length > 0) {
      await supabase.from('disposal_order_lines').insert(
        validLines.map(l => ({ disposal_order_id: doEditId, product_id: l.product_id, quantity: parseFloat(l.quantity) }))
      )
    }
    setDoSaving(false); setDoModal(false); setDoEditId(null)
    setDoForm(EMPTY_DO); setDoLines([{ product_id: '', quantity: '' }])
    load()
  }

  async function deleteDisposalOrder(id) {
    await supabase.from('disposal_order_lines').delete().eq('disposal_order_id', id)
    await supabase.from('disposal_orders').delete().eq('id', id)
    load()
  }

  function openEditDO(doo) {
    setDoEditId(doo.id)
    setDoForm({
      disposal_number: doo.disposal_number || '',
      from_location_id: doo.from_location_id || '',
      status: doo.status || 'draft',
      expected_completion_date: doo.expected_completion_date || '',
      actual_completion_date: doo.actual_completion_date || '',
      notes: doo.notes || '',
    })
    const lines = (doo.disposal_order_lines || []).map(l => ({ product_id: l.product_id || '', quantity: l.quantity || '' }))
    setDoLines(lines.length > 0 ? lines : [{ product_id: '', quantity: '' }])
    setDoModal(true)
  }
  const TO_STATUS_BADGE = { planned: 'neutral', shipped: 'info', cancelled: 'danger' }

  function exportInventoryTab() {
    const loc = locations.find(l => tab === `wh_${l.id}` || tab === `fba_${l.id}`)
    if (!loc) return
    const isFba = tab.startsWith('fba_')
    const visibleProducts = products.filter(p => p.product_type !== 'virtual_bundle' && (isFba ? p.amazon_enabled : true))
    const header = ['SKU', 'Name', 'On Hand', 'Allocated (WS)', 'Days on Hand', 'On Order', 'In Transit', 'Total Supply Days']
    const rows = visibleProducts.map(p => {
      const onHand = getOnHand(p.id, loc.id)
      const d = !p.shopify_enabled ? getAmazonDailyRate(p.id) : getShopifyDailyRate(p.id)
      const doh = d && onHand ? Math.round(onHand / d) : ''
      return [p.sku, p.name, onHand, getWholesaleAllocated(p.id), doh, getOnOrder(p.id), getInTransit(p.id, loc.id), getTotalSupplyDays(p.id) ?? '']
    })
    exportToCsv(`inventory-${loc.name.replace(/\s+/g, '-').toLowerCase()}.csv`, [header, ...rows])
  }

  function exportTransactions() {
    const header = ['Date', 'SKU', 'Product', 'Location', 'Type', 'Qty Change', 'Notes', 'By']
    const rows = transactions.map(t => [
      format(new Date(t.created_at), 'yyyy-MM-dd HH:mm'),
      t.products?.sku, t.products?.name, t.locations?.name,
      t.transaction_type, t.quantity, t.notes,
      t.user_profiles?.full_name || t.source
    ])
    exportToCsv('transactions.csv', [header, ...rows])
  }

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>

  const filteredTransferOrders = transferOrders.filter(t => toStatusFilter.includes(t.status))
  const filteredDisposalOrders = disposalOrders.filter(d => doStatusFilter.includes(d.status))

  return (
    <div className="page">
      <div className="page-header">
        <h1>{only === 'transfers' ? 'Transfer Orders' : only === 'locations' ? 'Locations' : 'Inventory'}<HelpLink section={only === 'transfers' ? 'transfer-orders' : only === 'locations' ? 'locations' : 'inventory'} /></h1>
        <div className="page-header-actions">
          {(tab.startsWith('wh_') || tab.startsWith('fba_')) && (
            <button className="btn btn-secondary" onClick={exportInventoryTab}><Download size={14} />Export CSV</button>
          )}
          {(tab.startsWith('wh_') || tab.startsWith('fba_') || tab === 'alerts') && (
            <>
              {!isBrandAdmin && <button className="btn btn-secondary" onClick={() => { setStrForm({ product_id: '', shopify_units_28d: '', amazon_units_14d: '' }); setStrModal(true) }}>
                <BarChart2 size={14} />Sell-Through Rates
              </button>}
              {!isBrandAdmin && <button className="btn btn-primary" onClick={() => { setAdjForm({ product_id: '', location_id: '', mode: 'new_qty', new_qty: '', adjustment: '', reason: 'correction', notes: '', lot_number: '', expiration_date: '' }); setAdjModal(true) }}>
                <Plus size={14} />Adjust Inventory
              </button>}
            </>
          )}
          {tab === 'locations' && (
            <button className="btn btn-primary" onClick={() => { setLocForm({ name: '', type: '3pl', address: '', notes: '' }); setLocEditing(null); setLocModal(true) }}>
              <Plus size={14} />Add Location
            </button>
          )}
          {tab === 'transfers' && (
            <>
              <StatusFilter statuses={TO_STATUSES} selected={toStatusFilter} onChange={setToStatusFilter} activeStatuses={TO_ACTIVE_STATUSES} />
              <button className="btn btn-primary" onClick={openNewTO}>
                <Plus size={14} />New Transfer
              </button>
            </>
          )}
          {tab === 'disposals' && (
            <>
              <StatusFilter statuses={DO_STATUSES} selected={doStatusFilter} onChange={setDoStatusFilter} activeStatuses={DO_ACTIVE_STATUSES} />
              <button className="btn btn-primary" onClick={() => { setDoEditId(null); setDoForm(EMPTY_DO); setDoLines([{ product_id: '', quantity: '' }]); setDoModal(true) }}>
                <Plus size={14} />New Disposal
              </button>
            </>
          )}
        </div>
      </div>

      {/* Dynamic Tabs */}
      {(() => {
        const warehouseLocs = locations.filter(l => ['1pl', '3pl'].includes(l.type))
        const fbaLocs = locations.filter(l => l.type === 'amazon_fba')
        const dynamicTabs = [
          ...warehouseLocs.map(l => ({ key: `wh_${l.id}`, label: l.name, type: 'warehouse', loc: l })),
          ...fbaLocs.map(l => ({ key: `fba_${l.id}`, label: l.name, type: 'fba', loc: l })),
          ...(isBrandAdmin ? [] : [
            { key: 'amazon_replen', label: 'Amazon Replenishment', type: 'amazon_replen' },
            { key: 'alerts', label: 'Alerts', type: 'alerts' },
          ]),
          ...(isBrandAdmin ? [] : [{ key: 'transactions', label: 'Transactions', type: 'transactions' }]),
          ...(isBrandAdmin ? [] : [
            { key: 'locations', label: 'Locations', type: 'locations' },
            { key: 'transfers', label: 'Transfer Orders', type: 'transfers' },
          ]),
          ...(isBrandAdmin ? [] : [{ key: 'disposals', label: 'Disposal Orders', type: 'disposals' }]),
        ]
        // Set default tab to first warehouse if still on placeholder
        if (tab === '__warehouse__' && dynamicTabs.length > 0) {
          setTimeout(() => setTab(dynamicTabs[0].key), 0)
        }
        if (only) return null
        return (
          <div style={{ display: 'flex', gap: '4px', borderBottom: '1px solid var(--border)', marginBottom: '4px', flexWrap: 'wrap' }}>
            {dynamicTabs.map(t => (
              <button key={t.key} onClick={() => setTab(t.key)} style={{ padding: '8px 16px', background: 'none', border: 'none', borderBottom: `2px solid ${tab === t.key ? 'var(--accent)' : 'transparent'}`, color: tab === t.key ? 'var(--accent)' : 'var(--text-3)', fontFamily: 'var(--font)', fontSize: '13px', fontWeight: 500, cursor: 'pointer', marginBottom: '-1px' }}>
                {t.label}
                {t.type === 'alerts' && allAlerts.length > 0 && (
                  <span style={{ marginLeft: '6px', background: 'var(--danger)', color: 'white', borderRadius: '10px', padding: '1px 6px', fontSize: '10px', fontWeight: 700 }}>
                    {allAlerts.length}
                  </span>
                )}
              </button>
            ))}
          </div>
        )
      })()}

      {/* ── WAREHOUSE VIEWS (one per warehouse location) ── */}
      {locations.filter(l => ['1pl', '3pl'].includes(l.type)).map(loc => (
        tab === `wh_${loc.id}` ? (
          <Fragment key={loc.id}>
          <div className="card">
            <div style={{ marginBottom: '12px' }}><UploadAsOf snapshots={snapshots} locationId={loc.id} /></div>
            {products.length > 0 && (
              <div style={{ position: 'relative', maxWidth: '320px', marginBottom: '16px' }}>
                <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }} />
                <input
                  className="input"
                  style={{ paddingLeft: '30px', paddingRight: search ? '30px' : undefined }}
                  placeholder="Search SKU, description or ASIN…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
                {search && (
                  <button
                    onClick={() => setSearch('')}
                    style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', display: 'flex' }}
                    aria-label="Clear search"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            )}
            {products.length === 0 ? (
              <div className="empty-state"><Package size={40} /><p>No products in catalog yet</p></div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      {[
                        { label: 'SKU', col: 'sku' },
                        { label: 'Description', col: 'name' },
                        { label: 'ASIN', col: 'asin' },
                        { label: 'On Hand Qty', col: 'onhand' },
                        { label: 'Allocated (WS)', col: 'wsalloc', ba: true },
                        { label: 'Days on Hand', col: 'doh', ba: true },
                        { label: 'On Order', col: 'onorder', ba: true },
                        { label: 'In Transit', col: 'intransit', ba: true },
                        { label: 'Total Supply Days', col: 'supply', ba: true },
                        { label: 'Reorder Alert', col: null, ba: true },
                      ].filter(c => !(c.ba && isBrandAdmin)).map(({ label, col }) => (
                        <th key={label} onClick={col ? () => toggleSort(col) : undefined} style={{ cursor: col ? 'pointer' : 'default', userSelect: 'none', whiteSpace: 'nowrap' }}>
                          {label}{col && sortIcon(col)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortProducts(
                      products.filter(p => p.product_type !== 'virtual_bundle' && p.product_type !== 'component' && matchesSearch(p)),
                      p => {
                        if (sortCol === 'sku') return p.sku
                        if (sortCol === 'name') return p.name
                        if (sortCol === 'asin') return p.asin
                        if (sortCol === 'onhand') return getOnHand(p.id, loc.id)
                        if (sortCol === 'wsalloc') return getWholesaleAllocated(p.id)
                        if (sortCol === 'doh') { const d = getShopifyDailyRate(p.id); const oh = getOnHand(p.id, loc.id); return d && oh ? Math.round(oh/d) : null }
                        if (sortCol === 'onorder') return getOnOrder(p.id)
                        if (sortCol === 'intransit') return getInTransit(p.id, loc.id)
                        if (sortCol === 'supply') return getTotalSupplyDays(p.id)
                        return null
                      }
                    ).map(p => {
                      const onHand = getOnHand(p.id, loc.id)
                      const inTransit = getInTransit(p.id)
                      const onOrder = getOnOrder(p.id)
                      const supplyDays = getTotalSupplyDays(p.id)
                      const reorder = getReorderAlert(p)
                      const leadTime = (p.production_lead_time_days || 45) + (p.sourcing_type === 'domestic' ? 7 : 45) + 30
                      return (
                        <tr key={p.id}>
                          <td style={{ whiteSpace: 'nowrap' }}><span className="mono">{p.sku}</span><CopyBtn text={p.sku} /></td>
                          <td style={{ fontWeight: 500, minWidth: '140px' }}>{p.name}</td>
                          <td style={{ whiteSpace: 'nowrap' }}><span className="mono" style={{ color: 'var(--text-2)' }}>{p.asin || '—'}</span></td>
                          <td style={{ fontWeight: 600 }}>
                            {onHand > 0 ? onHand : <span style={{ color: 'var(--text-3)' }}>0</span>}
                            {p.shopify_enabled === false && getLastManualUpdate(p.id, loc.id) && (
                              <div style={{ marginTop: '4px' }}>
                                <span className="badge badge-neutral" style={{ fontSize: '10px', fontWeight: 500, whiteSpace: 'nowrap' }} title="Manually entered — not synced from Shopify">
                                  Manual · updated {format(new Date(getLastManualUpdate(p.id, loc.id)), 'MMM d')}
                                </span>
                              </div>
                            )}
                          </td>
                          {!isBrandAdmin && <td style={{ color: 'var(--warning)' }} title="Units committed to open wholesale orders that haven't been removed from Shopify yet">{getWholesaleAllocated(p.id) || '—'}</td>}
                          {!isBrandAdmin && <td>{(() => { const d = !p.shopify_enabled ? getAmazonDailyRate(p.id) : getShopifyDailyRate(p.id); return d && onHand ? <span style={{ fontWeight: 600, color: Math.round(onHand/d) < 30 ? 'var(--danger)' : Math.round(onHand/d) < 60 ? 'var(--warning)' : 'var(--success)' }}>{Math.round(onHand/d)}d</span> : <span style={{ color: 'var(--text-3)' }}>—</span> })()}</td>}
                          {!isBrandAdmin && <td style={{ color: 'var(--accent)' }}>{onOrder || '—'}</td>}
                          {!isBrandAdmin && <td style={{ color: 'var(--info)' }}>{getInTransit(p.id, loc.id) || '—'}</td>}
                          {!isBrandAdmin && <td>{supplyDays !== null ? <span style={{ color: supplyDays < leadTime ? (supplyDays < leadTime - 30 ? 'var(--danger)' : 'var(--warning)') : 'var(--success)', fontWeight: 600 }}>{supplyDays}d</span> : <span style={{ color: 'var(--text-3)' }}>—</span>}</td>}
                          {!isBrandAdmin && <td>{(() => {
                            if (p.shopify_replen_status === 'sunset' && (!p.amazon_enabled || p.amazon_replen_status === 'sunset')) return <span className="badge badge-neutral" style={{ fontSize: '10px' }}>Sunset</span>
                            const str = sellThrough.find(s => s.product_id === p.id)
                            const hasSTR = str && (str.shopify_units_28d > 0 || str.amazon_units_14d > 0)
                            if (!hasSTR) return <span className="badge badge-neutral" style={{ fontSize: '10px', cursor: 'pointer' }} onClick={() => { setStrForm({ product_id: p.id, shopify_units_28d: '', amazon_units_14d: '' }); setStrModal(true) }}>+ Add STR</span>
                            if (!p.production_lead_time_days) return <span className="badge badge-neutral" style={{ fontSize: '10px' }}>+ Add Lead Time</span>
                            if (reorder) return <span className={`badge badge-${reorder.severity}`} style={{ fontSize: '10px', whiteSpace: 'nowrap' }}>⚠ Order {reorder.suggestedQty.toLocaleString()} units</span>
                            return <span style={{ color: 'var(--text-3)' }}>—</span>
                          })()}</td>}
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          {(() => {
            const locLots = allLots.filter(l => l.location_id === loc.id)
            if (locLots.length === 0) return null
            const today = new Date()

            function lotStatus(expiry) {
              if (!expiry) return 'sellable'
              const days = Math.round((new Date(expiry + 'T12:00:00') - today) / 86400000)
              if (days < 0) return 'expired'
              if (days <= 90) return 'atrisk'
              return 'sellable'
            }

            const grouped = {}
            locLots.forEach(lot => {
              const pid = lot.product_id
              if (!grouped[pid]) grouped[pid] = { sku: lot.products?.sku, name: lot.products?.name, lots: [] }
              grouped[pid].lots.push(lot)
            })
            const skuEntries = Object.entries(grouped).sort((a, b) => (a[1].sku || '').localeCompare(b[1].sku || ''))

            return (
              <div className="card" style={{ marginTop: '16px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px' }}>Lot Details — {locLots.length} lots across {skuEntries.length} SKUs</h3>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>SKU</th>
                        <th>Product</th>
                        <th>Lot #</th>
                        <th>Expiry Date</th>
                        <th>Status</th>
                        <th>Bin</th>
                        <th style={{ textAlign: 'right' }}>Lot Qty</th>
                        <th style={{ textAlign: 'right' }}>On Hand</th>
                      </tr>
                    </thead>
                    <tbody>
                      {skuEntries.map(([pid, { sku, name, lots }]) => {
                        const onHandQty = getOnHand(pid, loc.id)
                        const atRiskQty = lots.filter(l => lotStatus(l.expiration_date) === 'atrisk').reduce((s, l) => s + (l.quantity || 0), 0)
                        const expiredQty = lots.filter(l => lotStatus(l.expiration_date) === 'expired').reduce((s, l) => s + (l.quantity || 0), 0)
                        const sortedLots = [...lots].sort((a, b) => {
                          if (!a.expiration_date && !b.expiration_date) return 0
                          if (!a.expiration_date) return 1
                          if (!b.expiration_date) return -1
                          return a.expiration_date.localeCompare(b.expiration_date)
                        })
                        return sortedLots.map((lot, i) => {
                          const status = lotStatus(lot.expiration_date)
                          const daysLeft = lot.expiration_date ? Math.round((new Date(lot.expiration_date + 'T12:00:00') - today) / 86400000) : null
                          return (
                            <tr key={`${pid}-${i}`} style={{ borderTop: i === 0 ? '2px solid var(--border)' : undefined }}>
                              {i === 0 && (
                                <td rowSpan={sortedLots.length} style={{ verticalAlign: 'top', paddingTop: '10px' }}>
                                  <span className="mono" style={{ fontSize: '12px' }}>{sku}</span>
                                </td>
                              )}
                              {i === 0 && (
                                <td rowSpan={sortedLots.length} style={{ verticalAlign: 'top', paddingTop: '10px', fontWeight: 500, fontSize: '13px' }}>
                                  {name}
                                  {(atRiskQty > 0 || expiredQty > 0) && (
                                    <div style={{ marginTop: '4px', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                      {expiredQty > 0 && <span className="badge badge-danger" style={{ fontSize: '10px' }}>{expiredQty.toLocaleString()} expired</span>}
                                      {atRiskQty > 0 && <span className="badge badge-warning" style={{ fontSize: '10px' }}>{atRiskQty.toLocaleString()} at risk</span>}
                                    </div>
                                  )}
                                </td>
                              )}
                              <td><span className="mono" style={{ fontSize: '12px' }}>{lot.lot_number}</span></td>
                              <td style={{ fontSize: '12px', color: 'var(--text-2)' }}>{lot.expiration_date || <span style={{ color: 'var(--text-3)' }}>—</span>}</td>
                              <td>
                                {status === 'expired' && <span className="badge badge-danger" style={{ fontSize: '10px' }}>Expired</span>}
                                {status === 'atrisk' && <span className="badge badge-warning" style={{ fontSize: '10px' }}>Exp. {daysLeft}d</span>}
                                {status === 'sellable' && lot.expiration_date && <span style={{ color: 'var(--success)', fontSize: '12px' }}>OK</span>}
                                {status === 'sellable' && !lot.expiration_date && <span style={{ color: 'var(--text-3)', fontSize: '12px' }}>—</span>}
                              </td>
                              <td style={{ fontSize: '12px', color: 'var(--text-3)' }}>{lot.bin_location || '—'}</td>
                              <td style={{ textAlign: 'right', fontWeight: 600, fontSize: '13px' }}>{(lot.quantity || 0).toLocaleString()}</td>
                              {i === 0 && (
                                <td rowSpan={sortedLots.length} style={{ textAlign: 'right', verticalAlign: 'top', paddingTop: '10px' }}>
                                  <span style={{ fontWeight: 700, fontSize: '14px' }}>{onHandQty.toLocaleString()}</span>
                                  <div style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '2px' }}>Shopify on-hand</div>
                                </td>
                              )}
                            </tr>
                          )
                        })
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )
          })()}
          </Fragment>
        ) : null
      ))}

      {/* ── FBA VIEWS (one per FBA location) ── */}
      {locations.filter(l => l.type === 'amazon_fba').map(loc => (
        tab === `fba_${loc.id}` ? (
          <div className="card" key={loc.id}>
            <div style={{ marginBottom: '12px' }}><UploadAsOf snapshots={snapshots} locationId={loc.id} /></div>
            {products.length > 0 && (
              <div style={{ position: 'relative', maxWidth: '320px', marginBottom: '16px' }}>
                <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }} />
                <input
                  className="input"
                  style={{ paddingLeft: '30px', paddingRight: search ? '30px' : undefined }}
                  placeholder="Search SKU, description or ASIN…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
                {search && (
                  <button
                    onClick={() => setSearch('')}
                    style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', display: 'flex' }}
                    aria-label="Clear search"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            )}
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    {[
                      { label: 'SKU', col: 'sku' },
                      { label: 'Description', col: 'name' },
                      { label: 'ASIN', col: 'asin' },
                      { label: 'On Hand Qty', col: 'onhand' },
                      { label: 'Days on Hand', col: 'doh', ba: true },
                      { label: 'On Order', col: 'onorder', ba: true },
                      { label: 'In Transit', col: 'intransit', ba: true },
                      { label: 'FBA Target', col: null, ba: true },
                      { label: 'Transfer Alert', col: null, ba: true },
                    ].filter(c => !(c.ba && isBrandAdmin)).map(({ label, col }) => (
                      <th key={label} onClick={col ? () => toggleSort(col) : undefined} style={{ cursor: col ? 'pointer' : 'default', userSelect: 'none', whiteSpace: 'nowrap' }}>
                        {label}{col && sortIcon(col)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortProducts(
                    products.filter(p => (p.asin || p.amazon_enabled) && p.product_type !== 'component' && matchesSearch(p)),
                    p => {
                      if (sortCol === 'sku') return p.sku
                      if (sortCol === 'name') return p.name
                      if (sortCol === 'asin') return p.asin
                      if (sortCol === 'onhand') return getOnHand(p.id, loc.id)
                      if (sortCol === 'doh') { const d = getAmazonDailyRate(p.id); const oh = getOnHand(p.id, loc.id); return d && oh ? Math.round(oh/d) : null }
                      if (sortCol === 'onorder') return getOnOrder(p.id)
                      if (sortCol === 'intransit') return getInTransit(p.id, loc.id)
                      return null
                    }
                  ).map(p => {
                    const onHand = getOnHand(p.id, loc.id)
                    const dailyRate = getAmazonDailyRate(p.id)
                    const daysOnHand = dailyRate && onHand > 0 ? Math.round(onHand / dailyRate) : 0
                    const fbaTarget = p.fba_target_days || 60
                    const fbaAlert = getFBAAlert(p)
                    return (
                      <tr key={p.id}>
                        <td style={{ whiteSpace: 'nowrap' }}><span className="mono">{p.sku}</span></td>
                        <td style={{ fontWeight: 500, minWidth: '140px' }}>{p.name}</td>
                        <td style={{ whiteSpace: 'nowrap' }}><span className="mono" style={{ color: 'var(--text-2)' }}>{p.asin || '—'}</span></td>
                        <td style={{ fontWeight: 600 }}>{onHand > 0 ? onHand : <span style={{ color: 'var(--text-3)' }}>0</span>}</td>
                        {!isBrandAdmin && <td>{(() => { const d = getAmazonDailyRate(p.id); const days = d && onHand > 0 ? Math.round(onHand/d) : 0; return d && onHand > 0 ? <span style={{ fontWeight: 600, color: days < fbaTarget ? (days < 30 ? 'var(--danger)' : 'var(--warning)') : 'var(--success)' }}>{days}d</span> : <span style={{ color: 'var(--text-3)' }}>—</span> })()}</td>}
                        {!isBrandAdmin && <td style={{ color: 'var(--accent)' }}>{getOnOrder(p.id) || '—'}</td>}
                        {!isBrandAdmin && <td style={{ color: 'var(--info)' }}>{getInTransit(p.id, loc.id) || '—'}</td>}
                        {!isBrandAdmin && <td style={{ color: 'var(--text-3)', fontSize: '12px' }}>{fbaTarget}d</td>}
                        {!isBrandAdmin && <td>{(() => {
                          if (p.shopify_replen_status === 'sunset' && (!p.amazon_enabled || p.amazon_replen_status === 'sunset')) return <span className="badge badge-neutral" style={{ fontSize: '10px' }}>Sunset</span>
                          const str = sellThrough.find(s => s.product_id === p.id)
                          const hasSTR = str && (str.shopify_units_28d > 0 || str.amazon_units_14d > 0)
                          if (!hasSTR) return <span className="badge badge-neutral" style={{ fontSize: '10px', cursor: 'pointer' }} onClick={() => { setStrForm({ product_id: p.id, shopify_units_28d: '', amazon_units_14d: '' }); setStrModal(true) }}>+ Add STR</span>
                          if (fbaAlert) return <span className={`badge badge-${fbaAlert.severity}`} style={{ fontSize: '10px', whiteSpace: 'nowrap' }}>↑ Transfer {fbaAlert.suggestedQty.toLocaleString()} units</span>
                          return <span style={{ color: 'var(--text-3)' }}>—</span>
                        })()}</td>}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : null
      ))}

      {/* ── AMAZON REPLENISHMENT VIEW ── */}
      {tab === 'amazon_replen' && (() => {
        const replenLocs = [
          ...locations.filter(l => l.type === '3pl').sort((a, b) => a.name.localeCompare(b.name)),
          ...locations.filter(l => l.type === 'amazon_fba').sort((a, b) => a.name.localeCompare(b.name))
        ]
        const fbaLocIds = new Set(locations.filter(l => l.type === 'amazon_fba').map(l => l.id))
        const amazonProducts = sortProducts(
          products.filter(p => p.amazon_enabled),
          p => {
            if (sortCol === 'ar_sku') return p.sku
            if (sortCol === 'ar_name') return p.name
            if (sortCol && sortCol.startsWith('ar_loc_')) {
              const locId = sortCol.slice('ar_loc_'.length)
              return getOnHand(p.id, locId)
            }
            if (sortCol === 'ar_dayscover') {
              const fbaTotal = locations.filter(l => l.type === 'amazon_fba').reduce((sum, l) => sum + getOnHand(p.id, l.id), 0)
              const dailyRate = getAmazonDailyRate(p.id)
              return dailyRate && fbaTotal > 0 ? Math.round(fbaTotal / dailyRate) : null
            }
            if (sortCol === 'ar_prep') {
              return ['fba_prep_new_upc','fba_prep_ingredient_label','fba_prep_poly_bag','fba_prep_bubble_wrap','fba_prep_fragile_glass','fba_prep_suffocation_warning','fba_prep_expiration_label','fba_prep_hazmat'].filter(k => p[k]).length
            }
            return 0
          }
        )
        const PREP_BADGES = [
          { key: 'fba_prep_new_upc', short: 'UPC' },
          { key: 'fba_prep_ingredient_label', short: 'Ingr' },
          { key: 'fba_prep_poly_bag', short: 'Poly' },
          { key: 'fba_prep_bubble_wrap', short: 'Bubble' },
          { key: 'fba_prep_fragile_glass', short: 'Glass' },
          { key: 'fba_prep_suffocation_warning', short: 'Suffoc' },
          { key: 'fba_prep_expiration_label', short: 'Exp' },
          { key: 'fba_prep_hazmat', short: 'Hazmat' }
        ]
        return (
          <div className="card">
            {amazonProducts.length === 0 ? (
              <div className="empty-state"><Package size={40} /><p>No Amazon-enabled products. Toggle Amazon on a product in the Catalog page.</p></div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th onClick={() => toggleSort('ar_sku')} style={{ cursor: 'pointer' }}>SKU{sortIcon('ar_sku')}</th>
                      <th onClick={() => toggleSort('ar_name')} style={{ cursor: 'pointer' }}>Name{sortIcon('ar_name')}</th>
                      {replenLocs.map(l => (
                        <th key={l.id} onClick={() => toggleSort(`ar_loc_${l.id}`)} style={{ textAlign: 'right', cursor: 'pointer', color: fbaLocIds.has(l.id) ? 'var(--accent)' : undefined }}>
                          {l.name}{sortIcon(`ar_loc_${l.id}`)}
                          <div style={{ fontSize: '10px', fontWeight: 400, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            {l.type === 'amazon_fba' ? 'FBA' : '3PL'}
                          </div>
                        </th>
                      ))}
                      <th onClick={() => toggleSort('ar_dayscover')} style={{ textAlign: 'right', cursor: 'pointer' }}>FBA Days Cover{sortIcon('ar_dayscover')}</th>
                      <th onClick={() => toggleSort('ar_prep')} style={{ cursor: 'pointer' }}>Prep{sortIcon('ar_prep')}</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {amazonProducts.map(p => {
                      const fbaTotal = locations.filter(l => l.type === 'amazon_fba').reduce((sum, l) => sum + getOnHand(p.id, l.id), 0)
                      const dailyRate = getAmazonDailyRate(p.id)
                      const daysCover = dailyRate && fbaTotal > 0 ? Math.round(fbaTotal / dailyRate) : null
                      const fbaTarget = p.fba_target_days || 60
                      const flags = PREP_BADGES.filter(b => p[b.key])
                      return (
                        <tr key={p.id}>
                          <td><span className="mono">{p.sku}</span></td>
                          <td style={{ fontWeight: 500 }}>{p.name}</td>
                          {replenLocs.map(l => {
                            const qty = getOnHand(p.id, l.id)
                            return (
                              <td key={l.id} style={{ textAlign: 'right', fontWeight: qty > 0 ? 600 : 400, color: qty > 0 ? 'var(--text)' : 'var(--text-3)' }}>
                                {qty > 0 ? qty.toLocaleString() : '0'}
                              </td>
                            )
                          })}
                          <td style={{ textAlign: 'right' }}>
                            {daysCover === null ? <span style={{ color: 'var(--text-3)' }}>—</span>
                              : <span style={{ fontWeight: 600, color: daysCover < 30 ? 'var(--danger)' : daysCover < fbaTarget ? 'var(--warning)' : 'var(--success)' }}>{daysCover}d</span>}
                          </td>
                          <td>
                            {flags.length === 0 ? <span style={{ color: 'var(--text-3)', fontSize: '11px' }}>—</span> : (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
                                {flags.map(f => (
                                  <span key={f.key} className="badge badge-warning" style={{ fontSize: '10px' }}>{f.short}</span>
                                ))}
                              </div>
                            )}
                          </td>
                          <td style={{ fontSize: '12px', color: 'var(--text-2)', maxWidth: '240px' }}>{p.fba_prep_notes || ''}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )
      })()}

      {/* ── ALERTS VIEW ── */}
      {tab === 'alerts' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {allAlerts.length === 0 ? (
            <div className="card"><div className="empty-state" style={{ padding: '40px' }}><Package size={40} /><p style={{ color: 'var(--success)' }}>No alerts — inventory looks healthy</p></div></div>
          ) : (
            <>
              {/* Explanation */}
              <div className="card" style={{ padding: '14px 16px', background: 'var(--accent-bg)', borderColor: 'rgba(108,142,247,0.2)' }}>
                <p style={{ fontSize: '13px', color: 'var(--text-2)', lineHeight: 1.6 }}>
                  <strong style={{ color: 'var(--accent)' }}>How this works:</strong> Total Supply Days = all inventory everywhere. Net Supply Days = total supply minus the FBA transfer needed — this is the number that drives reorder decisions. If transferring to FBA would leave you short, you need to order more.
                </p>
              </div>

              <div className="card">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: 600 }}>All Inventory Alerts ({allAlerts.length})</h3>
                </div>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        {[
                          { label: 'SKU', col: 'sku' },
                          { label: 'Product', col: 'name' },
                          { label: 'Daily STR', col: 'daily' },
                          { label: 'On Hand', col: 'onhand' },
                          { label: 'In Transit', col: 'intransit' },
                          { label: 'On Order', col: 'onorder' },
                          { label: 'Total Supply', col: 'totalSupply' },
                          { label: 'FBA Days', col: 'fbaDays' },
                          { label: 'FBA Transfer', col: 'fbaNeeded' },
                          { label: 'Net Supply', col: 'netSupply' },
                          { label: 'Reorder?', col: null },
                        ].filter(c => !(c.ba && isBrandAdmin)).map(({ label, col }) => (
                          <th key={label} onClick={col ? () => toggleAlertSort(col) : undefined} style={{ cursor: col ? 'pointer' : 'default', userSelect: 'none', whiteSpace: 'nowrap' }}>
                            {label}{col && alertSortIcon(col)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sortAlerts(allAlerts).map(a => (
                        <tr key={a.product.id}>
                          <td style={{ whiteSpace: 'nowrap' }}><span className="mono">{a.product.sku}</span></td>
                          <td style={{ fontWeight: 500, minWidth: '160px' }}>{a.product.name}</td>
                          <td style={{ fontSize: '12px' }}>
                            <span style={{ color: 'var(--text-2)' }}>{a.daily.toFixed(1)}/day</span>
                            {a.bundleContext && a.bundleContext.length > 0 && (
                              <div style={{ fontSize: '10px', color: 'var(--accent)', marginTop: '2px' }}>
                                +{a.bundleContext.reduce((s, b) => s + b.addedDaily, 0).toFixed(1)} from {a.bundleContext.map(b => b.bundle.sku).join(', ')}
                              </div>
                            )}
                          </td>
                          <td>{a.totalOnHand || '—'}</td>
                          <td style={{ color: 'var(--info)' }}>{a.inTransit || '—'}</td>
                          <td style={{ color: 'var(--accent)' }}>{a.onOrder || '—'}</td>
                          <td>
                            <span style={{ fontWeight: 600, color: 'var(--text-2)' }}>{a.totalSupplyDays}d</span>
                          </td>
                          <td>
                            {a.fbaAlert
                              ? <span style={{ color: a.fbaSeverity === 'danger' ? 'var(--danger)' : 'var(--warning)', fontWeight: 600 }}>{a.fbaDays}d / {a.fbaTarget}d target</span>
                              : <span style={{ color: 'var(--success)' }}>{a.fbaDays}d ✓</span>
                            }
                          </td>
                          <td>
                            {a.fbaAlert
                              ? <span className={`badge badge-${a.fbaSeverity}`} style={{ fontSize: '11px' }}>↑ {a.fbaNeeded.toLocaleString()} units</span>
                              : <span style={{ color: 'var(--text-3)', fontSize: '12px' }}>—</span>
                            }
                          </td>
                          <td>
                            <span style={{ fontWeight: 600, color: a.reorderAlert ? (a.reorderSeverity === 'danger' ? 'var(--danger)' : 'var(--warning)') : 'var(--success)' }}>
                              {a.netSupplyDays}d
                            </span>
                            {a.fbaAlert && <span style={{ fontSize: '10px', color: 'var(--text-3)', display: 'block' }}>after FBA transfer</span>}
                          </td>
                          <td>
                            {a.reorderAlert
                              ? <span className={`badge badge-${a.reorderSeverity}`} style={{ fontSize: '11px', whiteSpace: 'nowrap' }}>⚠ Order {a.suggestedOrderQty.toLocaleString()}</span>
                              : <span style={{ color: 'var(--success)', fontSize: '12px' }}>✓ OK</span>
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── EXPIRY RISK VIEW (shown in alerts tab) ── */}
      {tab === 'alerts' && (() => {
        const today = new Date()
        const riskRows = allLots
          .filter(lot => lot.expiration_date)
          .map(lot => {
            const daysLeft = Math.round((new Date(lot.expiration_date + 'T12:00:00') - today) / 86400000)
            const str = sellThrough.find(s => s.product_id === lot.product_id)
            const dailyRate = str ? ((str.shopify_units_28d || 0) / 28) + ((str.amazon_units_14d || 0) / 14) : 0
            const projectedSold = Math.min(lot.quantity, Math.round(dailyRate * Math.max(0, daysLeft)))
            const atRisk = Math.max(0, lot.quantity - projectedSold)
            const atRiskPct = lot.quantity > 0 ? atRisk / lot.quantity : 0
            const product = products.find(p => p.id === lot.product_id)
            let severity
            if (daysLeft < 0) severity = 'expired'
            else if (atRiskPct > 0.5) severity = 'high'
            else if (atRiskPct > 0.2) severity = 'medium'
            else if (atRisk > 0) severity = 'low'
            else severity = 'ok'
            return { lot, product, daysLeft, dailyRate, projectedSold, atRisk, atRiskPct, severity }
          })
          .filter(r => r.severity !== 'ok')

        if (riskRows.length === 0) return null
        return (
          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: 600 }}>Expiry Risk — {riskRows.length} lot{riskRows.length !== 1 ? 's' : ''}</h3>
            </div>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '16px', fontSize: '12px', color: 'var(--text-3)' }}>
              <div><strong style={{ color: 'var(--text-2)' }}>At risk</strong> — units projected to expire unsold at today's sell rate</div>
              <div style={{ width: '1px', background: 'var(--border)' }} />
              {[
                { label: 'High', desc: '>50% of lot at risk', cls: 'badge-danger' },
                { label: 'Medium', desc: '20–50% at risk', cls: 'badge-warning' },
                { label: 'Low', desc: '<20% at risk', cls: 'badge-neutral' },
              ].map(({ label, desc, cls }) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span className={`badge ${cls}`} style={{ fontSize: '10px' }}>{label}</span>
                  {desc}
                </div>
              ))}
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    {[
                      { label: 'SKU', col: 'sku' },
                      { label: 'Product', col: 'name' },
                      { label: 'Lot #', col: 'lot' },
                      { label: 'Expiry', col: 'expiry' },
                      { label: 'Days Left', col: 'daysLeft' },
                      { label: 'Daily Rate', col: 'dailyRate' },
                      { label: 'Will Sell', col: 'projectedSold' },
                      { label: 'At Risk', col: 'atRisk' },
                      { label: 'Risk', col: 'severity' },
                    ].filter(c => !(c.ba && isBrandAdmin)).map(({ label, col }) => (
                      <th key={label} onClick={() => toggleExpirySort(col)} style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
                        {label}{expirySortIcon(col)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortExpiryRows(riskRows).map(({ lot, product, daysLeft, dailyRate, projectedSold, atRisk, atRiskPct, severity }, i) => (
                    <tr key={i}>
                      <td><span className="mono" style={{ fontSize: '12px' }}>{product?.sku}</span></td>
                      <td style={{ fontWeight: 500 }}>{product?.name}</td>
                      <td><span className="mono" style={{ fontSize: '12px' }}>{lot.lot_number}</span></td>
                      <td style={{ fontSize: '12px' }}>{lot.expiration_date}</td>
                      <td>
                        {daysLeft < 0
                          ? <span style={{ color: 'var(--danger)', fontWeight: 600 }}>Expired</span>
                          : <span style={{ fontWeight: 600, color: daysLeft <= 30 ? 'var(--danger)' : daysLeft <= 90 ? 'var(--warning)' : 'var(--text-2)' }}>{daysLeft}d</span>
                        }
                      </td>
                      <td style={{ fontSize: '12px', color: 'var(--text-2)' }}>
                        {dailyRate > 0 ? `${dailyRate.toFixed(1)}/day` : <span style={{ color: 'var(--text-3)' }}>—</span>}
                      </td>
                      <td style={{ fontSize: '13px' }}>
                        {dailyRate > 0
                          ? <><span style={{ color: 'var(--success)', fontWeight: 600 }}>{projectedSold.toLocaleString()}</span><span style={{ fontSize: '11px', color: 'var(--text-3)', marginLeft: '4px' }}>of {lot.quantity.toLocaleString()}</span></>
                          : <span style={{ color: 'var(--text-3)' }}>—</span>
                        }
                      </td>
                      <td>
                        <span style={{ fontWeight: 700, color: severity === 'expired' || severity === 'high' ? 'var(--danger)' : severity === 'medium' ? 'var(--warning)' : 'var(--text-2)' }}>
                          {atRisk.toLocaleString()}
                        </span>
                        <span style={{ fontSize: '11px', color: 'var(--text-3)', marginLeft: '4px' }}>({Math.round(atRiskPct * 100)}%)</span>
                      </td>
                      <td>
                        {severity === 'expired' && <span className="badge badge-danger" style={{ fontSize: '10px' }}>Expired</span>}
                        {severity === 'high' && <span className="badge badge-danger" style={{ fontSize: '10px' }}>High</span>}
                        {severity === 'medium' && <span className="badge badge-warning" style={{ fontSize: '10px' }}>Medium</span>}
                        {severity === 'low' && <span className="badge badge-neutral" style={{ fontSize: '10px' }}>Low</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      })()}

      {/* ── TRANSACTIONS TAB ── */}
      {tab === 'transactions' && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '16px' }}>
            <button className="btn btn-secondary btn-sm" onClick={exportTransactions}><Download size={13} />Export CSV</button>
            {!isBrandAdmin && <button className="btn btn-primary btn-sm" onClick={() => { setAdjForm({ product_id: '', location_id: '', mode: 'new_qty', new_qty: '', adjustment: '', reason: 'correction', notes: '', lot_number: '', expiration_date: '' }); setAdjModal(true) }}>
              <Plus size={13} />New Transaction
            </button>}
          </div>
          {transactions.length === 0 ? (
            <div className="empty-state"><p>No transactions yet</p></div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>Date</th><th>Product</th><th>Location</th><th>Type</th><th>Qty Change</th><th>Notes</th><th>By</th></tr>
                </thead>
                <tbody>
                  {transactions.map(t => (
                    <tr key={t.id}>
                      <td style={{ fontSize: '12px', color: 'var(--text-2)' }}>{format(new Date(t.created_at), 'MMM d, yyyy h:mm a')}</td>
                      <td><span className="mono" style={{ fontSize: '11px' }}>{t.products?.sku}</span> {t.products?.name}</td>
                      <td>{t.locations?.name || '—'}</td>
                      <td><span className="badge badge-neutral" style={{ fontSize: '10px' }}>{t.transaction_type.replace(/_/g, ' ')}</span></td>
                      <td style={{ fontWeight: 600, color: Number(t.quantity) >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                        {Number(t.quantity) >= 0 ? '+' : ''}{t.quantity}
                      </td>
                      <td style={{ color: 'var(--text-3)', fontSize: '12px' }}>{t.notes || '—'}</td>
                      <td style={{ fontSize: '12px', color: 'var(--text-3)' }}>{t.user_profiles?.full_name || t.source || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── LOCATIONS TAB ── */}
      {tab === 'locations' && (
        <div className="card">
          {locations.length === 0 ? (
            <div className="empty-state"><Warehouse size={40} /><p>No locations yet</p></div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Name</th><th>Type</th><th>Address</th><th>Notes</th><th></th></tr></thead>
                <tbody>
                  {locations.map(l => (
                    <tr key={l.id}>
                      <td style={{ fontWeight: 500 }}>{l.name}</td>
                      <td><span className="badge badge-neutral" style={{ fontSize: '11px' }}>{LOCATION_TYPES.find(t => t.value === l.type)?.label || l.type}</span></td>
                      <td style={{ color: 'var(--text-2)', fontSize: '12px' }}>{l.address || '—'}</td>
                      <td style={{ color: 'var(--text-3)', fontSize: '12px' }}>{l.notes || '—'}</td>
                      <td><button className="btn btn-ghost btn-sm" onClick={() => { setLocEditing(l); setLocForm({ name: l.name, type: l.type, address: l.address || '', notes: l.notes || '' }); setLocModal(true) }}><Edit2 size={13} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── TRANSFER ORDERS TAB ── */}
      {tab === 'transfers' && (
        <div className="card">
          {filteredTransferOrders.length === 0 ? (
            <div className="empty-state"><Truck size={40} /><p>{transferOrders.length === 0 ? 'No transfer orders yet' : 'No transfer orders match this filter'}</p></div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Transfer #</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Exp Ship</th>
                    <th>Ship Date</th>
                    <th>Exp Arrival</th>
                    <th>Carrier / Tracking</th>
                    <th>Items</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransferOrders.map(to => {
                    const lines = to.transfer_order_lines || []
                    const fmt = d => d ? format(new Date(d + 'T12:00:00'), 'MMM d, yyyy') : '—'
                    return (
                    <tr key={to.id}>
                      <td><span className="mono" style={{ fontSize: '13px' }}>{to.transfer_number}</span>{to.transfer_number && <CopyBtn text={to.transfer_number} />}</td>
                      <td>{to.from_loc?.name || '—'}</td>
                      <td><ArrowRight size={12} style={{ color: 'var(--text-3)', margin: '0 4px' }} />{to.to_loc?.name || '—'}</td>
                      <td><span className={`badge badge-${TO_STATUS_BADGE[to.status] || 'neutral'}`}>{TO_STATUS_LABEL[to.status] || to.status.replace(/_/g, ' ')}</span></td>
                      <td style={{ fontSize: '12px', color: 'var(--text-2)' }}>{to.created_at ? format(new Date(to.created_at), 'MMM d, yyyy') : '—'}</td>
                      <td style={{ fontSize: '12px' }}>{fmt(to.expected_ship_date)}</td>
                      <td style={{ fontSize: '12px' }}>{fmt(to.ship_date)}</td>
                      <td style={{ fontSize: '12px' }}>{fmt(to.expected_arrival)}</td>
                      <td style={{ fontSize: '12px' }}>
                        {to.carrier || to.tracking_number ? (
                          <span>{to.carrier || ''}{to.carrier && to.tracking_number ? ' · ' : ''}<span className="mono" style={{ fontSize: '11px' }}>{to.tracking_number || ''}</span></span>
                        ) : '—'}
                      </td>
                      <td style={{ minWidth: '160px', maxWidth: '220px' }}>
                        {lines.length === 0 ? <span style={{ color: 'var(--text-3)', fontSize: '12px' }}>—</span> : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                            {lines.map((l, i) => (
                              <span key={i} style={{ fontSize: '11px', lineHeight: 1.3 }}>
                                <span style={{ fontFamily: 'var(--mono)', fontSize: '11px', color: 'var(--accent)' }}>{l.products?.sku || '—'}</span>
                                {l.products?.name && <span style={{ color: 'var(--text-2)' }}> · {l.products.name.length > 18 ? l.products.name.slice(0, 18) + '…' : l.products.name}</span>}
                                <span style={{ color: 'var(--text-3)' }}> ({l.quantity})</span>
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button className="btn btn-ghost btn-sm" onClick={() => openEditTO(to)}><Edit2 size={13} /></button>
                          {to.status === 'planned' && <button className="btn btn-secondary btn-sm" style={{ whiteSpace: 'nowrap' }} onClick={() => markTransferShipped(to)} title={to.ship_date ? `Ship date ${to.ship_date}` : 'Ship date = today (edit to change)'}>Mark shipped</button>}
                        </div>
                      </td>
                    </tr>
                  )})}

                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── ADJUST INVENTORY MODAL ── */}
      {adjModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setAdjModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <h2>Adjust Inventory</h2>
              <button className="btn btn-ghost btn-sm" onClick={() => setAdjModal(false)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Product *</label>
                <ProductPicker
                  products={products}
                  value={adjForm.product_id}
                  onChange={id => setAdjForm(f => ({ ...f, product_id: id }))}
                  placeholder="Search for a product..."
                />
              </div>
              <div className="form-group">
                <label className="form-label">Location *</label>
                <select className="select" value={adjForm.location_id} onChange={e => setAdjForm(f => ({ ...f, location_id: e.target.value }))}>
                  <option value="">— Select location —</option>
                  {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
              {adjForm.product_id && adjForm.location_id && (
                <div style={{ padding: '10px 12px', background: 'var(--bg-3)', borderRadius: 'var(--radius)', fontSize: '13px', color: 'var(--text-2)' }}>
                  Current on-hand: <strong style={{ color: 'var(--text)' }}>{getOnHand(adjForm.product_id, adjForm.location_id)}</strong>
                </div>
              )}
              {(() => {
                const selP = products.find(p => p.id === adjForm.product_id)
                if (!selP || selP.shopify_enabled === false) return null
                return (
                  <div style={{ padding: '10px 12px', background: 'var(--bg-3)', border: '1px solid var(--warning)', borderRadius: 'var(--radius)', fontSize: '12px', color: 'var(--warning)', lineHeight: 1.4 }}>
                    ⚠ This item is synced from Shopify. The nightly sync will overwrite this manual change with the Shopify quantity. To change it permanently, update the quantity in Shopify instead.
                  </div>
                )
              })()}
              <div className="form-group">
                <label className="form-label">Adjustment Method</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', flex: 1, padding: '8px 12px', background: adjForm.mode === 'new_qty' ? 'var(--accent-bg)' : 'var(--bg-3)', borderRadius: 'var(--radius)', border: `1px solid ${adjForm.mode === 'new_qty' ? 'var(--accent)' : 'var(--border)'}` }}>
                    <input type="radio" checked={adjForm.mode === 'new_qty'} onChange={() => setAdjForm(f => ({ ...f, mode: 'new_qty' }))} />
                    Set new quantity
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', flex: 1, padding: '8px 12px', background: adjForm.mode === 'adjustment' ? 'var(--accent-bg)' : 'var(--bg-3)', borderRadius: 'var(--radius)', border: `1px solid ${adjForm.mode === 'adjustment' ? 'var(--accent)' : 'var(--border)'}` }}>
                    <input type="radio" checked={adjForm.mode === 'adjustment'} onChange={() => setAdjForm(f => ({ ...f, mode: 'adjustment' }))} />
                    Adjust by amount
                  </label>
                </div>
              </div>
              {adjForm.mode === 'new_qty' ? (
                <div className="form-group">
                  <label className="form-label">New Quantity</label>
                  <input className="input" type="number" value={adjForm.new_qty} onChange={e => setAdjForm(f => ({ ...f, new_qty: e.target.value }))} placeholder="0" />
                </div>
              ) : (
                <div className="form-group">
                  <label className="form-label">Adjustment Amount (use negative to subtract)</label>
                  <input className="input" type="number" value={adjForm.adjustment} onChange={e => setAdjForm(f => ({ ...f, adjustment: e.target.value }))} placeholder="+100 or -50" />
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Reason</label>
                <select className="select" value={adjForm.reason} onChange={e => setAdjForm(f => ({ ...f, reason: e.target.value }))}>
                  {REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Lot Number <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>(optional)</span></label>
                  <input className="input" value={adjForm.lot_number} onChange={e => setAdjForm(f => ({ ...f, lot_number: e.target.value }))} placeholder="LOT-12345" />
                </div>
                <div className="form-group">
                  <label className="form-label">Expiration Date <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>(optional)</span></label>
                  <input className="input" type="date" value={adjForm.expiration_date} onChange={e => setAdjForm(f => ({ ...f, expiration_date: e.target.value }))} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="textarea" rows={2} value={adjForm.notes} onChange={e => setAdjForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setAdjModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveAdjustment} disabled={adjSaving || !adjForm.product_id || !adjForm.location_id || (!adjForm.new_qty && !adjForm.adjustment)}>
                {adjSaving ? 'Saving...' : 'Save Adjustment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── LOCATION MODAL ── */}
      {locModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setLocModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <h2>{locEditing ? 'Edit Location' : 'Add Location'}</h2>
              <button className="btn btn-ghost btn-sm" onClick={() => setLocModal(false)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group"><label className="form-label">Name *</label><input className="input" value={locForm.name} onChange={e => setLocForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. ShipBob LA" /></div>
                <div className="form-group">
                  <label className="form-label">Type</label>
                  <select className="select" value={locForm.type} onChange={e => setLocForm(f => ({ ...f, type: e.target.value }))}>
                    {LOCATION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group"><label className="form-label">Address</label><input className="input" value={locForm.address} onChange={e => setLocForm(f => ({ ...f, address: e.target.value }))} /></div>
              <div className="form-group"><label className="form-label">Notes</label><textarea className="textarea" rows={2} value={locForm.notes} onChange={e => setLocForm(f => ({ ...f, notes: e.target.value }))} /></div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setLocModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveLocation} disabled={locSaving || !locForm.name}>{locSaving ? 'Saving...' : locEditing ? 'Update' : 'Add Location'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── SELL-THROUGH MODAL ── */}
      {strModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setStrModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <h2>Sell-Through Rate</h2>
              <button className="btn btn-ghost btn-sm" onClick={() => setStrModal(false)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Product *</label>
                <ProductPicker
                  products={products}
                  value={strForm.product_id}
                  onChange={id => {
                    const existing = sellThrough.find(s => s.product_id === id)
                    setStrForm(f => ({ ...f, product_id: id, shopify_units_28d: existing?.shopify_units_28d || '', amazon_units_14d: existing?.amazon_units_14d || '' }))
                  }}
                  placeholder="Search for a product..."
                />
              </div>
              <div className="form-group">
                <label className="form-label">Shopify Units Sold (last 28 days)</label>
                <input className="input" type="number" value={strForm.shopify_units_28d} onChange={e => setStrForm(f => ({ ...f, shopify_units_28d: e.target.value }))} placeholder="0" />
              </div>
              <div className="form-group">
                <label className="form-label">Amazon Units Sold (last 14 days)</label>
                <input className="input" type="number" value={strForm.amazon_units_14d} onChange={e => setStrForm(f => ({ ...f, amazon_units_14d: e.target.value }))} placeholder="0" />
              </div>
              {strForm.shopify_units_28d || strForm.amazon_units_14d ? (
                <div style={{ padding: '10px 12px', background: 'var(--accent-bg)', borderRadius: 'var(--radius)', fontSize: '13px', color: 'var(--text-2)' }}>
                  Combined daily rate: <strong style={{ color: 'var(--accent)' }}>
                    {((parseFloat(strForm.shopify_units_28d || 0) / 28) + (parseFloat(strForm.amazon_units_14d || 0) / 14)).toFixed(1)} units/day
                  </strong>
                </div>
              ) : null}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setStrModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveSellThrough} disabled={strSaving || !strForm.product_id}>{strSaving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── DISPOSAL ORDERS TAB ── */}
      {tab === 'disposals' && (
        <div className="card">
          {filteredDisposalOrders.length === 0 ? (
            <div className="empty-state"><Truck size={40} /><p>{disposalOrders.length === 0 ? 'No disposal orders yet' : 'No disposal orders match this filter'}</p></div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Disposal #</th>
                    <th>From</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Exp Completion</th>
                    <th>Actual Completion</th>
                    <th>Items</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDisposalOrders.map(doo => {
                    const lines = doo.disposal_order_lines || []
                    const fmt = d => d ? format(new Date(d + 'T12:00:00'), 'MMM d, yyyy') : '—'
                    return (
                      <tr key={doo.id}>
                        <td><span className="mono" style={{ fontSize: '13px' }}>{doo.disposal_number}</span></td>
                        <td>{doo.from_loc?.name || '—'}</td>
                        <td><span className={`badge badge-${DO_STATUS_BADGE[doo.status] || 'neutral'}`}>{doo.status}</span></td>
                        <td style={{ fontSize: '12px', color: 'var(--text-2)' }}>{doo.created_at ? format(new Date(doo.created_at), 'MMM d, yyyy') : '—'}</td>
                        <td style={{ fontSize: '12px' }}>{fmt(doo.expected_completion_date)}</td>
                        <td style={{ fontSize: '12px' }}>{fmt(doo.actual_completion_date)}</td>
                        <td style={{ minWidth: '160px', maxWidth: '220px' }}>
                          {lines.length === 0 ? <span style={{ color: 'var(--text-3)', fontSize: '12px' }}>—</span> : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                              {lines.map((l, i) => (
                                <span key={i} style={{ fontSize: '11px', lineHeight: 1.3 }}>
                                  <span style={{ fontFamily: 'var(--mono)', fontSize: '11px', color: 'var(--accent)' }}>{l.products?.sku || '—'}</span>
                                  {l.products?.name && <span style={{ color: 'var(--text-2)' }}> · {l.products.name.length > 18 ? l.products.name.slice(0, 18) + '…' : l.products.name}</span>}
                                  <span style={{ color: 'var(--text-3)' }}> ({l.quantity})</span>
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                        <td style={{ display: 'flex', gap: '4px' }}>
                          <button className="btn btn-ghost btn-sm" onClick={() => openEditDO(doo)}><Edit2 size={13} /></button>
                          <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }} onClick={() => deleteDisposalOrder(doo.id)}><Trash2 size={13} /></button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── DISPOSAL ORDER MODAL ── */}
      {doModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setDoModal(false)}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <h2>{doEditId ? 'Edit Disposal Order' : 'New Disposal Order'}</h2>
              <button className="btn btn-ghost btn-sm" onClick={() => { setDoModal(false); setDoEditId(null) }}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group"><label className="form-label">Disposal # *</label><input className="input" value={doForm.disposal_number} onChange={e => setDoForm(f => ({ ...f, disposal_number: e.target.value }))} placeholder="DISP-001" /></div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select className="select" value={doForm.status} onChange={e => setDoForm(f => ({ ...f, status: e.target.value }))}>
                    {DO_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">From Location</label>
                <select className="select" value={doForm.from_location_id} onChange={e => setDoForm(f => ({ ...f, from_location_id: e.target.value }))}>
                  <option value="">— Select —</option>
                  {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
              <div className="form-grid">
                <div className="form-group"><label className="form-label">Exp Completion Date</label><input className="input" type="date" value={doForm.expected_completion_date} onChange={e => setDoForm(f => ({ ...f, expected_completion_date: e.target.value }))} /></div>
                <div className="form-group"><label className="form-label">Actual Completion Date</label><input className="input" type="date" value={doForm.actual_completion_date} onChange={e => setDoForm(f => ({ ...f, actual_completion_date: e.target.value, status: e.target.value ? 'completed' : f.status }))} /></div>
              </div>

              <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px', marginTop: '4px' }}>Items</div>
              {doLines.map((line, i) => {
                const otherIds = doLines.filter((_, j) => j !== i).map(l => l.product_id).filter(Boolean)
                return (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '8px' }}>
                    <ProductPicker
                      products={products}
                      value={line.product_id}
                      onChange={id => setDoLines(ls => ls.map((l, j) => j === i ? { ...l, product_id: id } : l))}
                      disabledIds={otherIds}
                      placeholder="Search for a product..."
                    />
                    <input className="input" style={{ width: '100px' }} type="number" placeholder="Qty" value={line.quantity} onChange={e => setDoLines(ls => ls.map((l, j) => j === i ? { ...l, quantity: e.target.value } : l))} />
                  </div>
                  <button className="btn btn-ghost btn-sm" onClick={() => setDoLines(ls => ls.filter((_, j) => j !== i))} disabled={doLines.length === 1}>✕</button>
                </div>
                )
              })}
              <button className="btn btn-secondary btn-sm" onClick={() => setDoLines(ls => [...ls, { product_id: '', quantity: '' }])}><Plus size={12} />Add Item</button>

              <div className="form-group" style={{ marginTop: '12px' }}><label className="form-label">Notes</label><textarea className="textarea" rows={2} value={doForm.notes} onChange={e => setDoForm(f => ({ ...f, notes: e.target.value }))} /></div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => { setDoModal(false); setDoEditId(null) }}>Cancel</button>
              <button className="btn btn-primary" onClick={doEditId ? updateDisposalOrder : saveDisposalOrder} disabled={doSaving || !doForm.disposal_number}>
                {doSaving ? 'Saving...' : doEditId ? 'Save Changes' : 'Create Disposal'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── TRANSFER ORDER MODAL ── */}
      {toModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setToModal(false)}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <h2>{toEditId ? 'Edit Transfer Order' : 'New Transfer Order'}</h2>
              <button className="btn btn-ghost btn-sm" onClick={() => { setToModal(false); setToEditId(null) }}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group"><label className="form-label">Transfer # *</label><input className="input" value={toForm.transfer_number} onChange={e => setToForm(f => ({ ...f, transfer_number: e.target.value }))} placeholder="TO-001" /></div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select className="select" value={toForm.status} onChange={e => setToForm(f => ({ ...f, status: e.target.value }))}>
                    {TO_STATUSES.map(s => <option key={s} value={s}>{TO_STATUS_LABEL[s]}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">From Location *</label>
                  <select className="select" value={toForm.from_location_id} onChange={e => setToForm(f => ({ ...f, from_location_id: e.target.value }))}>
                    <option value="">— Select —</option>
                    {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">To Location *</label>
                  <select className="select" value={toForm.to_location_id} onChange={e => setToForm(f => ({ ...f, to_location_id: e.target.value }))}>
                    <option value="">— Select —</option>
                    {locations.filter(l => l.id !== toForm.from_location_id).map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-grid">
                <div className="form-group"><label className="form-label">Carrier</label><input className="input" placeholder="FedEx, UPS, DHL..." value={toForm.carrier} onChange={e => setToForm(f => ({ ...f, carrier: e.target.value }))} /></div>
                <div className="form-group"><label className="form-label">Tracking Number</label><input className="input" value={toForm.tracking_number} onChange={e => setToForm(f => ({ ...f, tracking_number: e.target.value }))} /></div>
              </div>
              <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>Dates</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-group"><label className="form-label">Exp Ship Date</label><input className="input" type="date" value={toForm.expected_ship_date} onChange={e => setToForm(f => ({ ...f, expected_ship_date: e.target.value }))} /></div>
                <div className="form-group"><label className="form-label">Actual Ship Date</label><input className="input" type="date" value={toForm.ship_date} onChange={e => setToForm(f => ({ ...f, ship_date: e.target.value }))} /></div>
                <div className="form-group"><label className="form-label">Exp Arrival</label><input className="input" type="date" value={toForm.expected_arrival} onChange={e => setToForm(f => ({ ...f, expected_arrival: e.target.value }))} /></div>
              </div>

              <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px', marginTop: '4px' }}>Items</div>
              {toLines.map((line, i) => {
                const otherIds = toLines.filter((_, j) => j !== i).map(l => l.product_id).filter(Boolean)
                return (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '8px' }}>
                    <ProductPicker
                      products={products}
                      value={line.product_id}
                      onChange={id => setToLines(ls => ls.map((l, j) => j === i ? { ...l, product_id: id } : l))}
                      disabledIds={otherIds}
                      placeholder="Search for a product..."
                    />
                    <input className="input" style={{ width: '100px' }} type="number" placeholder="Qty" value={line.quantity} onChange={e => setToLines(ls => ls.map((l, j) => j === i ? { ...l, quantity: e.target.value } : l))} />
                  </div>
                  <button className="btn btn-ghost btn-sm" onClick={() => setToLines(ls => ls.filter((_, j) => j !== i))} disabled={toLines.length === 1}>✕</button>
                </div>
                )
              })}
              <button className="btn btn-secondary btn-sm" onClick={() => setToLines(ls => [...ls, { product_id: '', quantity: '' }])}><Plus size={12} />Add Item</button>

              <div className="form-group" style={{ marginTop: '12px' }}><label className="form-label">Notes</label><textarea className="textarea" rows={2} value={toForm.notes} onChange={e => setToForm(f => ({ ...f, notes: e.target.value }))} /></div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => { setToModal(false); setToEditId(null) }}>Cancel</button>
              <button className="btn btn-primary" onClick={toEditId ? updateTransferOrder : saveTransferOrder} disabled={toSaving || !toForm.transfer_number || !toForm.from_location_id || !toForm.to_location_id}>
                {toSaving ? 'Saving...' : toEditId ? 'Save Changes' : 'Create Transfer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
